import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faHandshake, faShieldAlt, faGavel } from '@fortawesome/free-solid-svg-icons';

const TermsAndConditions = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-green-600 to-teal-700 text-white">
                <div className="max-w-7xl mx-auto px-4 py-16">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <FontAwesomeIcon icon={faFileContract} className="text-6xl text-green-200" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            Términos y Condiciones
                        </h1>
                        <p className="text-xl text-green-100 max-w-3xl mx-auto">
                            Condiciones de uso de la plataforma PetitPrime. Conoce tus derechos y obligaciones como usuario.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faHandshake} className="text-green-200" />
                                <span>Acuerdo de uso</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faGavel} className="text-green-200" />
                                <span>Legislación aplicable</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    {/* Introduction */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <FontAwesomeIcon icon={faHandshake} className="mr-3 text-green-600" />
                            1. Introducción
                        </h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Bienvenido a PetitPrime. Estos Términos y Condiciones de Uso ("Términos") rigen el uso de
                            nuestra plataforma de fidelización digital, incluyendo todos los servicios, funcionalidades
                            y contenido disponibles a través de https://petitprime.com y aplicaciones relacionadas.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            Al acceder o utilizar nuestros servicios, aceptas estar sujeto a estos Términos.
                            Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestros servicios.
                        </p>
                    </section>

                    {/* Definitions */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">2. Definiciones</h2>
                        <div className="space-y-4 text-gray-600">
                            <div>
                                <p><strong>"PetitPrime"</strong> se refiere a PetitPrime, S.L., empresa registrada en España.</p>
                            </div>
                            <div>
                                <p><strong>"Plataforma"</strong> se refiere al sitio web, aplicaciones móviles y servicios de PetitPrime.</p>
                            </div>
                            <div>
                                <p><strong>"Usuario"</strong> se refiere a cualquier persona que acceda o utilice la plataforma.</p>
                            </div>
                            <div>
                                <p><strong>"Cliente"</strong> se refiere a usuarios que utilizan la plataforma para gestionar programas de fidelización.</p>
                            </div>
                            <div>
                                <p><strong>"Servicios"</strong> se refiere a todas las funcionalidades y herramientas disponibles en la plataforma.</p>
                            </div>
                        </div>
                    </section>

                    {/* Account Registration */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">3. Registro y Cuenta de Usuario</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Para utilizar ciertos servicios de la plataforma, debes crear una cuenta de usuario:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Debes proporcionar información veraz, precisa y completa</li>
                            <li>Eres responsable de mantener la confidencialidad de tus credenciales</li>
                            <li>Eres responsable de todas las actividades que ocurran bajo tu cuenta</li>
                            <li>Debes notificar inmediatamente cualquier uso no autorizado</li>
                            <li>Una cuenta por persona física o jurídica</li>
                        </ul>
                    </section>

                    {/* Acceptable Use */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">4. Uso Aceptable</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Al utilizar nuestros servicios, te comprometes a:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Utilizar la plataforma únicamente para fines legales y legítimos</li>
                            <li>Respetar los derechos de otros usuarios y terceros</li>
                            <li>No realizar actividades que puedan dañar, inutilizar o deteriorar la plataforma</li>
                            <li>No intentar acceder a áreas restringidas o sistemas informáticos</li>
                            <li>No utilizar la plataforma para enviar spam o contenido malicioso</li>
                            <li>Cumplir con todas las leyes y regulaciones aplicables</li>
                        </ul>
                    </section>

                    {/* Services Description */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">5. Descripción de Servicios</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            PetitPrime proporciona una plataforma integral de fidelización digital que incluye:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Gestión de programas de lealtad y sistemas de puntos</li>
                            <li>Herramientas de comunicación y marketing con clientes</li>
                            <li>Sistema de recompensas, descuentos y premios</li>
                            <li>Analytics y reportes de fidelización</li>
                            <li>Integración con sistemas POS y CRM</li>
                            <li>Herramientas de gamificación y engagement</li>
                            <li>Gestión de encuestas y feedback de clientes</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed">
                            Nos reservamos el derecho de modificar, suspender o discontinuar cualquier servicio
                            en cualquier momento sin previo aviso.
                        </p>
                    </section>

                    {/* Payment Terms */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">6. Términos de Pago</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Los servicios de PetitPrime están sujetos a planes de suscripción:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Los precios se facturan por adelantado en base mensual o anual</li>
                            <li>Los pagos son no reembolsables excepto según lo establecido en la política de reembolso</li>
                            <li>Nos reservamos el derecho de cambiar los precios con 30 días de aviso previo</li>
                            <li>El incumplimiento del pago puede resultar en la suspensión del servicio</li>
                            <li>Todos los precios incluyen IVA según la legislación aplicable</li>
                        </ul>
                    </section>

                    {/* Intellectual Property */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">7. Propiedad Intelectual</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            La plataforma PetitPrime y todo su contenido son propiedad de PetitPrime, S.L. o sus licenciantes:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Software, código fuente y aplicaciones</li>
                            <li>Diseños, interfaces y elementos gráficos</li>
                            <li>Marcas comerciales, logotipos y nombres comerciales</li>
                            <li>Contenido, documentación y materiales de formación</li>
                            <li>Bases de datos, algoritmos y know-how</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed">
                            Se otorga una licencia limitada, no exclusiva y revocable para utilizar la plataforma
                            únicamente para los fines autorizados.
                        </p>
                    </section>

                    {/* Privacy and Data */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">8. Privacidad y Datos</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            El tratamiento de datos personales se rige por nuestra Política de Privacidad,
                            que forma parte integral de estos Términos:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Cumplimos con el RGPD y la LOPDGDD</li>
                            <li>Recopilamos datos necesarios para proporcionar nuestros servicios</li>
                            <li>Implementamos medidas de seguridad apropiadas</li>
                            <li>Respetamos tus derechos de protección de datos</li>
                            <li>No vendemos ni compartimos datos con terceros sin consentimiento</li>
                        </ul>
                    </section>

                    {/* Limitation of Liability */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">9. Limitación de Responsabilidad</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            En la máxima medida permitida por la ley:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>PetitPrime no será responsable de daños indirectos, incidentales o consecuenciales</li>
                            <li>La responsabilidad total está limitada al importe pagado por los servicios en los últimos 12 meses</li>
                            <li>No garantizamos la disponibilidad ininterrumpida del servicio</li>
                            <li>No garantizamos resultados específicos de fidelización</li>
                            <li>No somos responsables de datos perdidos o corrompidos</li>
                        </ul>
                    </section>

                    {/* Termination */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">10. Terminación</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Estos Términos permanecen en vigor hasta que sean terminados:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Puedes cancelar tu cuenta en cualquier momento</li>
                            <li>Podemos suspender o terminar el acceso por incumplimiento</li>
                            <li>La terminación no afecta las obligaciones ya contraídas</li>
                            <li>Los datos pueden ser eliminados tras la terminación</li>
                        </ul>
                    </section>

                    {/* Governing Law */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">11. Ley Aplicable y Jurisdicción</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Estos Términos se rigen por la legislación española:
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                            <li>Se aplica la legislación española y europea</li>
                            <li>Jurisdicción exclusiva de los Juzgados y Tribunales de Barcelona</li>
                            <li>Se respetan los derechos del consumidor</li>
                            <li>Las cláusulas nulas no afectan el resto del acuerdo</li>
                        </ul>
                    </section>

                    {/* Modifications */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">12. Modificaciones</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Nos reservamos el derecho de modificar estos Términos en cualquier momento.
                            Los cambios serán notificados a través de la plataforma o por correo electrónico.
                            El uso continuado tras la modificación implica la aceptación de los nuevos términos.
                        </p>
                    </section>

                    {/* Contact Information */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">13. Información de Contacto</h2>
                        <div className="bg-gray-50 rounded-lg p-6">
                            <p className="text-gray-600 mb-4">
                                Para cualquier consulta sobre estos Términos y Condiciones:
                            </p>
                            <div className="space-y-2 text-gray-600">
                                <p><strong>Email:</strong> legal@petitstores.com</p>
                                <p><strong>Teléfono:</strong> +34 900 123 456</p>
                                <p><strong>Dirección:</strong> Barcelona, España</p>
                                <p><strong>Horario:</strong> Lunes a Viernes, 9:00 - 18:00 (CET)</p>
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="text-center bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            ¿Tienes preguntas sobre nuestros términos?
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Nuestro equipo legal está aquí para ayudarte con cualquier consulta.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/privacy"
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
                            >
                                Ver Política de Privacidad
                            </Link>
                            <Link
                                to="/legal"
                                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
                            >
                                Ver Aviso Legal
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default TermsAndConditions;
